import React from "react";
import { Drawer } from "@design-system/Drawer";
import { BANK_RECONCILIATION_SOURCE_TYPES } from "@common/constants/history/bank-reconciliation-source-types";
import { Loader } from "@design-system/Loader/Loader";
import { SearchFilter } from "@design-system/SearchFilter/SearchFilter";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { BankReconciliation } from "@common/contracts/reconciliation.contract";
import { FinancialAccount } from "@common/types/financialAccount";
import { AggregatedFilters } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFilters";
import { AggregatedFiltersProvider } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFiltersProvider";
import {
    BreadcrumbItem,
    Breadcrumbs,
} from "@design-system/Breadcrumbs/Breadcrumbs";
import { Pagination } from "@design-system/Pagination/Pagination";
import { useAuditLogData } from "@components/auditLog/hooks/useAuditLogData";
import { ReconciliationLog } from "./ReconciliationLog";
import { ReconciliationLogFilters } from "./ReconciliationLogFilters";
import styles from "./ReconciliationLogDrawer.module.scss";

interface ReconciliationLogDrawerProps {
    open: boolean;
    onClose: () => void;
    reconciliation: BankReconciliation;
    financialAccount: FinancialAccount;
    reconciliationNumber: number;
}

export const ReconciliationLogDrawer: React.FC<
    ReconciliationLogDrawerProps
> = ({
    open,
    onClose,
    reconciliation,
    financialAccount,
    reconciliationNumber,
}) => {
    const {
        auditLogs,
        isLoading,
        currentPage,
        totalPages,
        setCurrentPage,
        userFilters,
        searchTerm,
        userFiltersChangeHandler,
        searchTermChangeHandler,
    } = useAuditLogData({
        sourceTypes: BANK_RECONCILIATION_SOURCE_TYPES,
        sourceId: reconciliation.id,
        enabled: open,
    });

    const headerContent = React.useMemo(() => {
        const accountName = financialAccount.name ?? "Account";
        const reconciliationLabel = `Reconciliation #${reconciliationNumber + 1}`;
        return (
            <div className={styles.headerContent}>
                <Breadcrumbs className={styles.accountInfo}>
                    <BreadcrumbItem>{accountName}</BreadcrumbItem>
                    <BreadcrumbItem current>
                        {reconciliationLabel}
                    </BreadcrumbItem>
                </Breadcrumbs>
                <div className={styles.headerActions}>
                    <SearchFilter
                        filter={searchTerm}
                        onChange={(value) =>
                            searchTermChangeHandler(value ?? "")
                        }
                        placeholder="Search history..."
                        minLength={1}
                    />
                    <AggregatedFiltersProvider defaultTab="filters">
                        <AggregatedFilters>
                            <ReconciliationLogFilters
                                filters={userFilters}
                                onChange={userFiltersChangeHandler}
                            />
                        </AggregatedFilters>
                    </AggregatedFiltersProvider>
                </div>
            </div>
        );
    }, [
        financialAccount.name,
        reconciliationNumber,
        userFilters,
        userFiltersChangeHandler,
        searchTerm,
        searchTermChangeHandler,
    ]);
    const isEmpty = auditLogs.length === 0;
    const content = React.useMemo(() => {
        if (isLoading) {
            return (
                <div className={styles.loaderWrapper}>
                    <Loader />
                </div>
            );
        }
        if (isEmpty) {
            return (
                <div className={styles.emptyWrapper}>
                    <EmptyState
                        header={
                            searchTerm
                                ? "No matching activity found"
                                : "No activity available"
                        }
                    />
                </div>
            );
        }
        return (
            <>
                <div className={styles.scrollableWrapper}>
                    {auditLogs.map((log) => (
                        <ReconciliationLog key={log.id} log={log} />
                    ))}
                </div>
                {totalPages > 1 && (
                    <div className={styles.paginationWrapper}>
                        <Pagination
                            currentPage={currentPage}
                            pageCount={totalPages}
                            onPageChange={setCurrentPage}
                        />
                    </div>
                )}
            </>
        );
    }, [
        isLoading,
        isEmpty,
        auditLogs,
        searchTerm,
        totalPages,
        currentPage,
        setCurrentPage,
    ]);

    return (
        <Drawer
            open={open}
            onClose={onClose}
            size="quarter"
            noPadding
            headerAddonLeft={headerContent}
        >
            <div className={styles.content}>{content}</div>
        </Drawer>
    );
};
